import { en, type TranslationKey } from './en';
import { ru } from './ru';

export type Locale = 'ru' | 'en';
const SUPPORTED: Locale[] = ['ru', 'en'];
const STORAGE_KEY = 'tubr.locale';
const DEFAULT_LOCALE: Locale = 'ru';

const dicts: Record<Locale, typeof en> = { en, ru };

let currentLocale: Locale = readStoredLocale();

function readStoredLocale(): Locale {
  const v = localStorage.getItem(STORAGE_KEY);
  if (v === 'ru' || v === 'en') return v;
  return DEFAULT_LOCALE;
}

export function getLocale(): Locale {
  return currentLocale;
}

export function setLocale(locale: Locale): void {
  if (locale === currentLocale) return;
  if (!SUPPORTED.includes(locale)) return;
  currentLocale = locale;
  localStorage.setItem(STORAGE_KEY, locale);
  for (const handler of changeHandlers) handler();
}

const changeHandlers = new Set<() => void>();
export function onLocaleChange(handler: () => void): () => void {
  changeHandlers.add(handler);
  return () => changeHandlers.delete(handler);
}

export function t(key: TranslationKey, params?: Record<string, string | number>): string {
  const dict = dicts[currentLocale];
  let s: string = dict[key] ?? en[key] ?? key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      s = s.replaceAll(`{${k}}`, String(v));
    }
  }
  return s;
}

// Russian plural rule (1 / 2-4 / 5+).
export function pluralRu(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

// "5 players" / "1 player" / "5 игроков" / etc. Generic helper for the few
// plural-sensitive strings (currently only player counts).
export function tPlural(n: number, oneKey: TranslationKey, manyKey: TranslationKey, fewKey?: TranslationKey): string {
  if (currentLocale === 'ru') {
    const form = pluralRu(n, oneKey, fewKey ?? manyKey, manyKey);
    return t(form as TranslationKey, { n });
  }
  return t(n === 1 ? oneKey : manyKey, { n });
}
